import * as React from 'react'
import { 
  Sidebar, 
  SidebarHeader, 
  SidebarContent, 
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton
} from '../layouts/sidebar'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Button } from '../ui/button'
import { LucideIcon, Settings } from 'lucide-react'

export interface QuickSidebarProps {
  title: string
  subtitle?: string
  logo?: React.ReactNode
  user?: {
    name: string
    role: string
    avatar?: string
    initials: string
  }
  navigation: Array<{
    label: string
    icon: LucideIcon
    isActive?: boolean
    badge?: string
  }>
  sections?: Array<{
    label: string
    icon: LucideIcon
    iconColor?: 'gold' | 'purple' | 'blue' | 'green' | 'orange'
    items: Array<{
      label: string
      icon: LucideIcon
      isActive?: boolean
      badge?: string
    }>
  }>
  className?: string
}

const iconColorMap = {
  gold: 'text-agentela-gold',
  purple: 'text-agentela-purple',
  blue: 'text-agentela-blue', 
  green: 'text-agentela-green',
  orange: 'text-agentela-orange'
}

export const QuickSidebar = React.forwardRef<HTMLDivElement, QuickSidebarProps>(
  ({ title, subtitle, logo, user, navigation, sections, className, ...props }, ref) => {
    return (
      <Sidebar ref={ref} className={className} {...props}>
        <SidebarHeader>
          <div className="flex items-center space-x-3">
            {logo || (
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">
                  {title.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-sidebar-foreground">{title}</h2>
              {subtitle && (
                <p className="text-xs text-sidebar-muted-foreground">{subtitle}</p>
              )}
            </div>
          </div>
        </SidebarHeader>
        
        <SidebarContent>
          {/* Main Navigation */}
          <SidebarGroup>
            <SidebarMenu>
              {navigation.map((item, index) => (
                <SidebarMenuItem key={index}>
                  <SidebarMenuButton isActive={item.isActive}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto text-xs bg-sidebar-accent text-sidebar-accent-foreground px-1.5 py-0.5 rounded">
                        {item.badge}
                      </span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>

          {/* Sections */}
          {sections?.map((section, sectionIndex) => (
            <SidebarGroup key={sectionIndex}>
              <SidebarGroupLabel>
                <section.icon className={`h-3 w-3 mr-2 ${iconColorMap[section.iconColor || 'gold']}`} />
                {section.label}
              </SidebarGroupLabel>
              <SidebarMenu>
                {section.items.map((item, itemIndex) => (
                  <SidebarMenuItem key={itemIndex}>
                    <SidebarMenuButton isActive={item.isActive}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto text-xs bg-sidebar-accent text-sidebar-accent-foreground px-1.5 py-0.5 rounded">
                          {item.badge}
                        </span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>

        {user && (
          <SidebarFooter>
            <div className="flex items-center space-x-3 px-2 py-2 mb-3">
              <Avatar className="h-9 w-9">
                {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
                <AvatarFallback>{user.initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user.name}
                </p>
                <p className="text-xs text-sidebar-muted-foreground truncate">
                  {user.role}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </SidebarFooter>
        )}
      </Sidebar>
    )
  }
)

QuickSidebar.displayName = 'QuickSidebar'
